import { useState } from 'react';
import type { PropuestaDetail } from '@/types';
import { useUIStore } from '@/store/uiStore';
import {
  avance,
  avanceCompromisos,
  healthIniciativa,
  hitosDeIniciativa,
  hitosPorLente,
  iniciativaTieneBloqueo,
  tagsHeredados,
} from '@/lib/propuesta-selectors';
import styles from './facility-tabs.module.css';

interface Props {
  detail: PropuestaDetail;
  tagsById: Map<string, { nombre: string; color: string }>;
}

export function IniciativasView({ detail, tagsById }: Props) {
  const lente = useUIStore((s) => s.lente);
  const scope = useUIStore((s) => s.scope);
  const setScope = useUIStore((s) => s.setScope);
  const clearScope = useUIStore((s) => s.clearScope);
  const [evFilter, setEvFilter] = useState<string>('');

  const evs = detail.entregasValor;
  const evById = Object.fromEntries(evs.map((e) => [e.id, e]));

  const inis = evFilter
    ? detail.iniciativas.filter((i) => i.entregaValorId === evFilter)
    : detail.iniciativas;

  return (
    <>
      {evs.length > 1 && (
        <div className={styles.toolbar}>
          <h2 className={styles.toolbarTitle}>Iniciativas</h2>
          <select
            value={evFilter}
            onChange={(e) => setEvFilter(e.target.value)}
            className={styles.select}
          >
            <option value="">Todas las Entregas de Valor</option>
            {evs.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.codigo} · {ev.nombre}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className={styles.grid}>
        {inis.map((ini) => {
          const ev = ini.entregaValorId ? evById[ini.entregaValorId] : null;
          const compromisos = detail.compromisos.filter((c) => c.iniciativaId === ini.id);
          const hitos = hitosPorLente(hitosDeIniciativa(detail, ini.id), lente);
          const av = avance(hitos);
          const avComp = avanceCompromisos(detail, compromisos.map((c) => c.id));
          const iniTags = tagsHeredados(detail, 'ini', ini.id);
          const scoped = scope.type === 'ini' && scope.id === ini.id;
          const hasBloqueo = iniciativaTieneBloqueo(detail, ini.id);
          const health = healthIniciativa(detail, ini.id);
          const healthCls =
            health === 'red'
              ? styles.cardBloqueado
              : health === 'yellow'
                ? styles.cardHealthYellow
                : health === 'green'
                  ? styles.cardHealthGreen
                  : '';

          return (
            <div
              key={ini.id}
              className={`${styles.iniCard} ${scoped ? styles.scoped : ''} ${healthCls}`}
              onClick={() => (scoped ? clearScope() : setScope({ type: 'ini', id: ini.id }))}
            >
              <div className={styles.iniHead}>
                <div className={styles.iniTitleRow}>
                  <span className={styles.iniBadge}>{ini.codigo}</span>
                  <span className={styles.iniTitle}>{ini.nombre}</span>
                </div>
              </div>
              {ev && (
                <div className={styles.iniEv} style={{ color: ev.color }}>
                  <span className={styles.dot} style={{ background: ev.color }} />
                  {ev.codigo} · {ev.nombre}
                </div>
              )}
              {ini.valor && <div className={styles.iniValor} style={{ marginTop: 8 }}>{ini.valor}</div>}

              <div className={styles.iniProgress}>
                <div className={styles.iniProgressLbl}>
                  <span>Hitos · {lente}</span>
                  <span>
                    {av.done}/{av.total} · {av.pct}%
                  </span>
                </div>
                <div className={styles.iniBar}>
                  <div
                    className={styles.iniFill}
                    style={{ width: `${av.pct}%`, background: 'var(--brand)' }}
                  />
                </div>
              </div>

              <div className={styles.iniProgress} style={{ marginTop: 8 }}>
                <div className={styles.iniProgressLbl}>
                  <span>Compromisos</span>
                  <span>
                    {avComp.done}/{avComp.total} · {avComp.pct}%
                  </span>
                </div>
                <div className={styles.iniBar}>
                  <div
                    className={styles.iniFill}
                    style={{
                      width: `${avComp.pct}%`,
                      background: 'var(--brand)',
                      opacity: 0.55,
                    }}
                  />
                </div>
              </div>

              {iniTags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
                  {iniTags.map((tid) => {
                    const t = tagsById.get(tid);
                    if (!t) return null;
                    return (
                      <span
                        key={tid}
                        style={{
                          background: t.color + '22',
                          color: t.color,
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '.04em',
                        }}
                      >
                        {t.nombre}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
